import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { DigitalAccount } from '@amfam/shared/digital-account/data-access/lib/+state/digital-account/digital-account.model';
import { DigitalAccountService } from '@amfam/shared/digital-account/data-access/lib/services/digital-account.service';
import { Party } from '@amfam/shared/models/lib/party/party';
import { PartyEmail } from '@amfam/shared/models/lib/party/party-email';
import { UpdateBusinessName, UpdateForgerockId } from '@amfam/shared/user/lib/+state/user.actions';
import {
  selectCustomerId,
  selectPartyDetails,
  selectUserState,
  selectWaid
} from '@amfam/shared/user/lib/+state/user.selectors';
import { selectCustomerGroupId } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import { CommunicationService } from '@amfam/shared/utility/shared-services/lib/communication/communication.service';

import { UpdateEmailData } from '../models/email-validation.model';
import { EmailValidationResponse } from '../models/profile.model';
import { AlternateAuthService } from '../services/alternate-auth.service';
import { DigitalServiceProgramService } from '../services/digital-service-program.service';
import { PartyService } from '../services/party.service';
import { ProfileUtilService } from '../services/profile-util.service';
import {
  AddContactEntryAction,
  AddContactEntrySuccessAction,
  ChangeContactEntryAction,
  ChangeContactEntrySuccessAction,
  ChangeContactPhoneAction,
  ContactEntryFailAction,
  DeleteContactEntryAction,
  DeleteContactEntrySuccessAction,
  EmailNotificationFailAction,
  EmailNotificationSuccessAction,
  FetchDaDataFailAction,
  FetchDaDataSuccessAction,
  FetchDataAction,
  FetchPartyDataFailAction,
  FetchPartyDataSuccessAction,
  GetValidationStatusForEmailAction,
  GetValidationStatusForEmailFailAction,
  GetValidationStatusForPendingEmailAction,
  GetValidationStatusForPendingEmailSuccessAction,
  MakeRequestAction,
  ProfileLoad,
  RefreshPartyDataAction,
  UpdateContactMethodValuesAction,
  UpdateDaAction,
  UpdateDaFailAction,
  UpdateDaSuccessAction,
  UpdateEmailAction,
  UpdateEmailFailAction,
  UpdateEmailSuccessAction,
  UpdateMembership,
  UpdateMembershipFailAction,
  UpdateMembershipSuccessAction,
  ValidateEmailAction,
  ValidateEmailFailAction,
  ValidateEmailSuccessAction
} from './profile.actions';

/**
 * Dispatches a FETCH_DATA action when the account is loaded
 */
export const loadAccountEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ProfileLoad),
      map(action => action.payload),
      map(payload => FetchDataAction(payload))
    );
  },
  { functional: true }
);

/**
 * gets Party data from the store
 * if the authenticatin payload contains a customerId
 */
export const loadPartyEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(FetchDataAction),
      map(action => action.payload),
      filter(payload => {
        // Make sure we have a customerId before calling getPartyDetails()
        return (payload?.customerId ?? '') !== '';
      }),
      switchMap(() =>
        store.select(selectPartyDetails).pipe(
          map(res => FetchPartyDataSuccessAction(res)),
          catchError(err => of(FetchPartyDataFailAction(err)))
        )
      )
    );
  },
  { functional: true }
);

/**
 * Add an email or phone to a Party
 */
export const addContactEntryEffect = createEffect(
  (actions$ = inject(Actions), partyService = inject(PartyService)) => {
    return actions$.pipe(
      ofType(AddContactEntryAction),
      map(action => action.payload),
      switchMap(
        (data): Observable<Action> =>
          partyService.refreshPartyVersion().pipe(
            switchMap(() => partyService.addContactEntry(data)),
            map(res => {
              res.methodType = data.methodType;
              return res.status.code === 200
                ? UpdateContactMethodValuesAction(data)
                : ContactEntryFailAction(res);
            }),
            catchError(err => {
              err.methodType = data.methodType;
              return of(ContactEntryFailAction(err));
            })
          )
      )
    );
  },
  { functional: true }
);

/**
 * This happens after you ADD a new email of phone.
 * We need to GET all emails or phones again so we
 * have the ID of the entry we just added.
 */
export const getUpdatedValuesEffect = createEffect(
  (
    actions$ = inject(Actions),
    partyService = inject(PartyService),
    profileUtilService = inject(ProfileUtilService)
  ) => {
    return actions$.pipe(
      ofType(UpdateContactMethodValuesAction),
      map(action => action.payload),
      switchMap(payload =>
        partyService.getPartyDetails().pipe(
          mergeMap((res: Party) => {
            if (payload.methodType === 'emails' && payload.data.setPrimaryEmail) {
              const validationPayload = profileUtilService.getOldAndNewPrimaryEmail(
                payload.data,
                res
              );
              return [
                AddContactEntrySuccessAction({
                  setPrimaryEmail: true,
                  setPrimaryPhone: false,
                  methodType: payload.methodType,
                  [payload.methodType]: res[payload.methodType]
                }),
                GetValidationStatusForEmailAction(validationPayload)
              ];
            }
            return [
              AddContactEntrySuccessAction({
                setPrimaryEmail: false,
                setPrimaryPhone: payload.methodType === 'phones' && payload.data.primaryIndicator,
                methodType: payload.methodType,
                [payload.methodType]: res[payload.methodType]
              })
            ];
          }),
          catchError(err => {
            err.methodType = payload?.methodType;
            return of(ContactEntryFailAction(err));
          })
        )
      )
    );
  },
  { functional: true }
);

/**
 * Dispatch ChangeContactPhoneAction after AddContactEntrySuccessAction
 * Send email updating the phone and setting it primary
 */
export const dispatchChangeContactPhone = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(AddContactEntrySuccessAction),
      map(action => action.payload),
      filter(payload => payload.methodType === 'phones' && payload.setPrimaryPhone),
      map(payload => {
        const data = payload.phones ?? [];
        return ChangeContactPhoneAction({
          methodType: payload.methodType,
          data
        });
      })
    );
  },
  { functional: true }
);

export const validationEffect = createEffect(
  (
    actions$ = inject(Actions),
    dspService = inject(DigitalServiceProgramService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(GetValidationStatusForEmailAction),

      map(action => action.payload),
      switchMap(payload => {
        return dspService.getValidationStatus(payload?.newEntry.emailAddress ?? '').pipe(
          mergeMap((response: EmailValidationResponse) => {
            if (response.emailProfile.confirmed) {
              let customerId: string | undefined = '';
              store
                .select(selectCustomerId)
                .pipe(take(1))
                .subscribe(customerId1 => {
                  customerId = customerId1 ? customerId1 : undefined;
                });

              const updateData: DigitalAccount = {
                customerId: customerId,
                emailAddress: payload?.newEntry.emailAddress
              };
              return [
                MakeRequestAction(), // start the spinner
                UpdateDaAction(updateData)
              ];
            } else {
              return [
                MakeRequestAction(), // start the spinner
                ValidateEmailAction({
                  // email has not been validated
                  newEmail: payload?.newEntry,
                  oldEmail: payload?.oldEntry
                })
              ];
            }
          }),
          catchError(error => of(GetValidationStatusForEmailFailAction(error)))
        );
      })
    );
  },
  { functional: true }
);

/**
 * Delete an email or phone from a party
 */
export const deleteContactEntryEffect = createEffect(
  (actions$ = inject(Actions), partyService = inject(PartyService)) => {
    return actions$.pipe(
      ofType(DeleteContactEntryAction),
      map(action => action.payload),
      switchMap(
        (data): Observable<Action> =>
          partyService.refreshPartyVersion().pipe(
            switchMap(() => partyService.deleteContactEntry(data)),
            map(res => {
              res.methodType = data.methodType;
              return res.status.code === 200
                ? DeleteContactEntrySuccessAction(data)
                : ContactEntryFailAction(res);
            }),
            catchError(err => {
              err.methodType = data?.methodType;
              return of(ContactEntryFailAction(err));
            })
          )
      )
    );
  },
  { functional: true }
);

/**
 * Update an email or phone in a party
 */
export const changeContactEntryEffect = createEffect(
  (actions$ = inject(Actions), partyService = inject(PartyService)) => {
    return actions$.pipe(
      ofType(ChangeContactEntryAction),
      map(action => action.payload),
      switchMap((data): Observable<Action> => {
        // oldEntry will exist if the user is changing a preferred contact setting that has a pre-existing value
        // in this situation the old and new values have to be PUT separately
        // first update should be the old entry if there is one
        const entry = data.data.oldEntry ? data.data.oldEntry : data.data.newEntry;
        // if there is an old entry, second update will be the new entry
        const entry2 = data.data.oldEntry ? data.data.newEntry : undefined;
        // 'oldEntry' key present = component-initiated dispatch; absent = effect-chained second PUT
        // (the first PUT response already refreshed this.partyVersion via editContactEntry)
        const refresh$ =
          'oldEntry' in data.data ? partyService.refreshPartyVersion() : of(undefined);
        return refresh$.pipe(
          switchMap(() => partyService.editContactEntry(entry, data.methodType, entry2)),
          map(res => {
            if (res.status.code === 200) {
              if (entry2) {
                // kick off edit for second entry
                return ChangeContactEntryAction({
                  data: { newEntry: entry2 },
                  methodType: data.methodType
                });
              } else {
                return ChangeContactEntrySuccessAction({
                  methodType: data.methodType,
                  data: res.otherEntry ? [entry, res.otherEntry] : [entry]
                });
              }
            }
            res.methodType = data.methodType;
            return ContactEntryFailAction(res);
          }),
          catchError(err => {
            err.methodType = data.methodType;
            return of(ContactEntryFailAction(err));
          })
        );
      })
    );
  },
  { functional: true }
);

/**
 * Refresh party details from the party API after all CRUD operations on a party
 */
export const refreshPartyEffect = createEffect(
  (actions$ = inject(Actions), partyService = inject(PartyService)) => {
    return actions$.pipe(
      ofType(
        RefreshPartyDataAction,
        ChangeContactEntrySuccessAction,
        DeleteContactEntrySuccessAction
      ),
      mergeMap(() =>
        partyService.getPartyDetails().pipe(
          map(res => FetchPartyDataSuccessAction(res)),
          catchError(err => of(FetchPartyDataFailAction(err)))
        )
      )
    );
  },
  { functional: true }
);

export const loadDigitalAccountEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    daService = inject(DigitalAccountService)
  ) => {
    return actions$.pipe(
      ofType(FetchDataAction),
      concatLatestFrom(() => store.select(selectWaid)),
      switchMap(([, waid]) =>
        daService.getDigitalAccountByWaid(waid).pipe(
          switchMap(res => {
            const actions: Action[] = [];
            actions.push(FetchDaDataSuccessAction(res));
            return actions;
          }),
          catchError(err => of(FetchDaDataFailAction(err)))
        )
      )
    );
  },
  { functional: true }
);

export const updateDigitalAccountEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    daService = inject(DigitalAccountService)
  ) => {
    return actions$.pipe(
      ofType(UpdateDaAction),
      map(action => action.payload),
      concatLatestFrom(() => [store.select(selectWaid)] satisfies [Observable<unknown>]),
      switchMap(([data, waid]) =>
        daService.updateDigitalAccount(data, waid).pipe(
          mergeMap(() => {
            // we now need to dispatch two actions
            // 1. to update the UI by calling getPartyDetails -> called by RefreshPartyDataAction
            // 2. to update the state with UpdateDaSuccessAction
            return [RefreshPartyDataAction(), UpdateDaSuccessAction(data)];
          }),
          catchError(err => of(UpdateDaFailAction(err)))
        )
      )
    );
  },
  { functional: true }
);

/**
 * This effect is used to update MyAccount email when using AltAuth
 */
export const updateMyAccountEmailEffect = createEffect(
  (actions$ = inject(Actions), alternateAuthService = inject(AlternateAuthService)) => {
    return actions$.pipe(
      ofType(UpdateEmailAction),
      map(action => action.payload),
      switchMap((data: UpdateEmailData) =>
        alternateAuthService.updateEmail(data).pipe(
          map(() => UpdateEmailSuccessAction(data)),
          catchError(err => of(UpdateEmailFailAction(err)))
        )
      )
    );
  },
  { functional: true }
);

/**
 * Email the user when something changes
 */
export const sendNotificationEmailEffect = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    communicationService = inject(CommunicationService),
    applicationService = inject(ApplicationService)
  ) => {
    const isAdminApp = applicationService.isApp(Applications.MYACCOUNT_ADMIN);
    return actions$.pipe(
      ofType(ChangeContactEntrySuccessAction, ChangeContactPhoneAction),
      map(action => action.payload),
      concatLatestFrom(() => [store.select(selectUserState)] satisfies [Observable<unknown>]),
      switchMap(([data, userState]) => {
        const customerIdValue =
          isAdminApp && userState.mdmId ? userState.mdmId : (userState.customerId ?? '');
        return communicationService
          .sendEmailNotificationForProfile(
            data.methodType,
            null,
            customerIdValue,
            userState.emailAddress ?? '',
            userState.firstName
          )
          .pipe(
            map(() => EmailNotificationSuccessAction()),
            catchError(err => of(EmailNotificationFailAction(err)))
          );
      })
    );
  },
  { functional: true }
);

/**
 * Send an email to the new email address
 * with a link to confirm the new address
 */
export const validateEmailEffect = createEffect(
  (
    actions$ = inject(Actions),
    dspService = inject(DigitalServiceProgramService),
    applicationService = inject(ApplicationService)
  ) => {
    const isAdminApp = applicationService.isApp(Applications.MYACCOUNT_ADMIN);
    return actions$.pipe(
      ofType(ValidateEmailAction),
      map(action => action.payload),
      switchMap(data =>
        dspService.validate(data.newEmail?.emailAddress ?? '', isAdminApp ? 'EMAIL OTP' : '').pipe(
          map(res =>
            res.status && res.status.code === 200
              ? ValidateEmailSuccessAction(isAdminApp)
              : ValidateEmailFailAction()
          ),
          catchError(err => of(ValidateEmailFailAction(err)))
        )
      )
    );
  },
  { functional: true }
);

/**
 *  New email status for Make Primary
 */
export const emailStatusEffect = createEffect(
  (actions$ = inject(Actions), dspService = inject(DigitalServiceProgramService)) => {
    return actions$.pipe(
      ofType(GetValidationStatusForPendingEmailAction),
      switchMap(() => {
        return dspService.getValidationStatus('').pipe(
          mergeMap((response: EmailValidationResponse) => {
            if (response?.emailProfile?.confirmed) {
              return [RefreshPartyDataAction()];
            } else {
              return [
                MakeRequestAction(),
                GetValidationStatusForPendingEmailSuccessAction({
                  // email has not been validated
                  newEmail: new PartyEmail(response.emailProfile.emailAddress, 'MYACCOUNT'),
                  tokenExpiry: response.emailProfile.tokenExpired
                })
              ];
            }
          }),
          catchError(error => of(GetValidationStatusForEmailFailAction(error)))
        );
      })
    );
  },
  { functional: true }
);

export const updateMembershipEffect = createEffect(
  (actions$ = inject(Actions), profileUtilService = inject(ProfileUtilService)) => {
    return actions$.pipe(
      ofType(UpdateMembership),
      map(action => action.payload),
      switchMap(
        // POST updated membership details
        (data): Observable<Action> =>
          forkJoin(profileUtilService.editMembership(data)).pipe(
            map(() => {
              return UpdateMembershipSuccessAction(data);
            }),
            catchError(err => {
              return of(UpdateMembershipFailAction(err));
            })
          )
      )
    );
  },
  { functional: true }
);

/**
 * Update business name in user store when digital account data is fetched (MyAccount app)
 * Note: MyAccount-Admin uses the shared effect in digital-account.effects.ts
 */
export const updateBusinessNameFromProfileEffect = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FetchDaDataSuccessAction),
      map(action => action.payload),
      filter(payload => !!payload.nameDetail?.businessName),
      map(payload => UpdateBusinessName(payload.nameDetail?.businessName ?? '')),
      catchError((err, caught) => {
        return caught; // resubscribe to restart the stream
      })
    );
  },
  { functional: true }
);

/**
 * Populates UserState.forgerockId from the digital account record (the matching ciamUsers
 * entry, else forgeRockId). Leaves it unset when the record has neither.
 */
export const updateForgerockIdFromProfileEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(FetchDaDataSuccessAction),
      map(action => action.payload),
      concatLatestFrom(() => store.select(selectCustomerGroupId)),
      map(
        ([payload, customerGroupId]) =>
          payload.ciamUsers?.find(ciamUser => ciamUser.customerGroupId === customerGroupId)?.frId ??
          payload.forgeRockId
      ),
      filter((frId): frId is string => !!frId),
      map(frId => UpdateForgerockId(frId)),
      catchError((err, caught) => {
        return caught; // resubscribe to restart the stream
      })
    );
  },
  { functional: true }
);
